/*!
 * strobe.js — strobe(): flashing on the beat
 * © 2026 Rob Canning — GPLv3
 *
 *     strobe(8)                       the stage white 8 times a beat, on half of each
 *     strobe(8, duty:0.25)            ... on a quarter of each
 *     strobe(4, color:#000)           black (any colour)
 *     strobe(2, invert:1)             the stage turned to its negative instead
 *     bar > strobe(16)                that thing on and off, 16 times a beat
 *     strobe(8, mask:spot)            flashing only inside the shape spot (hole:spot, everywhere but)
 *     strobe(off)  /  bar > strobe(off)
 * Ctrl+Enter on a strobe line turns it round: strobe(8) runs and becomes
 * strobe(8, off:1), which stops it and turns back (liveEval.js flipToggle).
 *
 * Locked to the shared beat grid, which runs with what is heard (beatGrid.js):
 * a flash at the start of each part of the beat, every machine in a room
 * together. A frame decides: faster than the screen's frames (half the frame
 * rate) it cannot show. Ctrl+, (still) stops every strobe.
 *
 * Caution: flashing light can bring on seizures in people with photosensitive
 * epilepsy; fast full-screen strobes most of all.
 */

import { gridNow, beatAt } from "./audio/beatGrid.js";
import { setPassing } from "../system/screenFx.js";
import { isMoving, bindParam, releaseBinding } from "../control/paramBinding.js";
import { maskFromArgs, setMask } from "../system/stageMask.js";
import { onReset } from "../system/teardown.js";

const strobes = new Map();          // key ("stage" or a thing's id) -> { n, duty, apply(on), off() }
let raf = 0;

function frame() {
  raf = 0;
  if (!strobes.size) return;
  const b = beatAt(gridNow().ms);
  for (const s of strobes.values()) {
    const p = b * s.n;
    const on = s.n > 0 && p - Math.floor(p) < s.duty;
    if (on !== s.was) { s.was = on; s.apply(on); }
  }
  raf = requestAnimationFrame(frame);
}

/** A veil over the stage, under the console, in a colour (turned back in dark mode, so it shows as written). */
function veil(colour) {
  const el = document.createElement("div");
  el.className = "oscilla-strobe";
  const dark = document.documentElement.style.filter.includes("invert");
  el.style.cssText = `position:fixed;inset:0;pointer-events:none;z-index:10039;opacity:0;background:${colour};${dark ? "filter:invert(1) hue-rotate(180deg);" : ""}`;
  document.body.appendChild(el);
  return el;
}

function stop(key) {
  const s = strobes.get(key);
  if (!s) return;
  s.off();
  releaseBinding(`strobe:${key}:n`);
  strobes.delete(key);
}

/** Every strobe stopped (still(), a new example). */
export function stopAllStrobes() {
  for (const key of [...strobes.keys()]) stop(key);
}
onReset("stage", stopAllStrobes);

export function handleStrobeCue(ast, cueElement = null) {
  const args = ast?.args || [];
  const arg = k => args.find(a => a.type === k)?.value;
  const target = cueElement && cueElement.id ? cueElement : null;
  const key = target ? target.id : "stage";
  const amount = arg("amount") ?? arg("rate");
  if (arg("off") != null || /^(off|0)$/i.test(String(amount ?? ""))) { stop(key); return; }
  stop(key);

  const duty = Math.min(0.95, Math.max(0.05, Number(arg("duty")) || 0.5));
  let apply, off;
  if (target) {
    const was = target.style.visibility;
    apply = on => { target.style.visibility = on ? was : "hidden"; };
    off = () => { target.style.visibility = was; };
  } else if (arg("invert") != null && !/^(0|off)$/i.test(String(arg("invert")))) {
    apply = on => setPassing("invert", on ? 1 : 0);
    off = () => setPassing("invert", 0);
  } else {
    const v = veil(String(arg("color") ?? arg("colour") ?? "#ffffff"));
    // by opacity, not display: the layer keeps its size, so a mask on it is there for the first flash
    apply = on => { v.style.opacity = on ? "1" : "0"; };
    // mask:spot / hole:spot -- the flash only inside a shape (or a text's letters), or everywhere but
    const masked = maskFromArgs(`strobe:${key}`, () => [v], arg);
    off = () => { if (masked) setMask(`strobe:${key}`, () => [v], null); v.remove(); };
  }
  const s = { n: 0, duty, was: null, apply, off };
  // how many a beat: a number, or moving (follow(), sinOsc())
  if (isMoving(amount)) bindParam(amount, v => { s.n = Math.max(0, v); }, { owner: `strobe:${key}:n`, min: 0, max: 64, default: 4 });
  else s.n = Math.max(0, Math.min(64, Number(amount) || 4));
  strobes.set(key, s);
  if (!raf) raf = requestAnimationFrame(frame);
}
